/**
 * Zustand slice for the MARS Live panel's UI state.
 *
 * Only holds *client* state -- server state (vintage list, multi fetches,
 * expected returns) lives in TanStack Query. Keeps the selected vintage and
 * asset toggles, plus the window timeframe for the ER + volatility panes.
 */
import { create } from 'zustand';
import type { UiAsset } from './types';

// 10y/20y were dropped from the UI so the Portfolio controls fit one row;
// re-widen this union if long windows come back.
export type Timeframe = '0.5y' | '1y' | '3y';

export type LiveFundId =
  | 'mars-fqm'
  | 'mars-dar-nn'
  | 'mars-fidelity-balanced'
  | 'mars-fidelity-conservative';

interface MarsLiveState {
  selectedVintage: string | null;
  selectedAssets: ReadonlySet<UiAsset>;
  timeframe: Timeframe;
  fundId: LiveFundId;
  volTiming: boolean;
  transactionCosts: boolean;
  constraints: boolean;

  setSelectedVintage: (date: string) => void;
  toggleAsset: (asset: UiAsset) => void;
  setTimeframe: (tf: Timeframe) => void;
  setFundId: (id: LiveFundId) => void;
  setVolTiming: (v: boolean) => void;
  setTransactionCosts: (v: boolean) => void;
  setConstraints: (v: boolean) => void;
}

export const useMarsLiveStore = create<MarsLiveState>((set) => ({
  selectedVintage: null,
  selectedAssets: new Set<UiAsset>(['Equities']),
  timeframe: '1y',
  fundId: 'mars-fqm',
  volTiming: true,
  transactionCosts: true,
  constraints: true,

  setSelectedVintage: (date) => set({ selectedVintage: date }),

  toggleAsset: (asset) =>
    set((s) => {
      const next = new Set(s.selectedAssets);
      if (next.has(asset)) {
        if (next.size <= 1) return s; // at least one asset must remain selected
        next.delete(asset);
      } else {
        next.add(asset);
      }
      return { selectedAssets: next };
    }),

  setTimeframe: (tf) => set({ timeframe: tf }),

  setFundId: (id) =>
    set({ fundId: id, volTiming: true, transactionCosts: true, constraints: true }),

  // Vol Timing OFF forces Transaction Costs OFF — the data routing in
  // `mars_vintage_db_service` only ships tc_off_sv_off when vol timing is
  // disabled, so allowing TC=on with VT=off would request a config that
  // does not exist on the backend.
  setVolTiming: (v) => set(v ? { volTiming: true } : { volTiming: false, transactionCosts: false }),
  setTransactionCosts: (v) =>
    set((s) => (s.volTiming ? { transactionCosts: v } : { transactionCosts: false })),
  setConstraints: (v) => set({ constraints: v }),
}));
